import styled from 'styled-components'
import { Button, theme } from '@notino/react-styleguide'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'

import { BREAKPOINTS, RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT } from '../../../styles/constants'
import {
	NotinoBodySmall,
	NotinoLabelRegular,
	NotinoLabelRegular400,
	NotinoLabelRegular500,
	NotinoLabelSmall,
	NotinoLabelSmall400,
	NotinoTitleSmall,
	resetButtonStyles
} from '../../../styles/helpers'
import RenderPrice from '../../RenderPrice/RenderPrice'
import ClockIcon from '../../../assets/icons/ClockIcon'

export const SummaryLayoutWrapper = styled.div`
	display: flex;
	overflow: hidden;
	height: 100%;
	flex-direction: column;

	@media (min-width: ${BREAKPOINTS.lg}) {
		flex-direction: row;
	}
`

export const ScrollableContent = styled(OverlayScrollbarsComponent)`
	overflow: auto;

	@media (min-width: ${BREAKPOINTS.lg}) {
		flex: 1;
		min-width: 0;
		min-height: 0;
		position: relative;
		z-index: 1;
		transition: height 0.3s ease;
	}
`

export const SummaryWrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
`

export const SummaryGrid = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;

	@media (min-width: ${BREAKPOINTS.lg}) {
		flex-direction: row;
		flex: 1;
	}
`

export const CloseButton = styled.button`
	${resetButtonStyles};
	position: absolute;
	right: 20px;
	top: 20px;
	width: 24px;
	height: 24px;
`

export const PromoBanner = styled.div`
	order: 2;

	@media (min-width: ${BREAKPOINTS.lg}) {
		width: 400px;
		order: 1;
		padding: 0;
		border-radius: 0;
	}
`

export const PromoBannerContent = styled.div`
	@media (min-width: ${BREAKPOINTS.lg}) {
		position: sticky;
		top: 0;
	}
`

export const Picture = styled.picture`
	display: block;
	width: 100%;
	aspect-ratio: 500 / 960;

	img {
		width: 100%;
		height: 100%;
		display: block;
	}

	@media (min-width: ${BREAKPOINTS.lg}) {
		aspect-ratio: 400 / 720;
	}
`

export const PromoBannerLink = styled.a`
	text-decoration: none;
	display: block;
`

export const ReservationContent = styled.div`
	width: 100%;
	padding: 20px;
	order: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 20px;

	@media (min-width: ${BREAKPOINTS.lg}) {
		width: 520px;
		order: 2;
	}
`

export const CheckIcon = styled.div`
	width: 60px;
	height: 60px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;
	background: ${theme.color.background.highlight};
`

export const ReservationTitleWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
`

export const ReservationTitle = styled.h2`
	${NotinoTitleSmall};
	margin-bottom: 16px;
	text-align: center;
	outline: none;
`

export const ReservationWrapper = styled.div`
	padding: 10px;
	border-radius: 4px;
	display: flex;
	position: relative;
	flex-direction: column;
	gap: 10px;
	@media (min-width: ${BREAKPOINTS.lg}) {
		gap: 16px;
	}
`

export const SalonInfo = styled.div`
	display: flex;
	gap: 12px;
`
export const SalonCover = styled.div`
	width: 56px;
	height: 56px;
	img {
		width: 56px;
		height: 56px;
		object-fit: cover;
		border-radius: 4px;
		display: block;
	}
`

export const Rating = styled.div`
	display: flex;
	${NotinoLabelSmall};
	align-items: center;
	gap: 4px;
	color: ${theme.color.text.primary};
	margin-bottom: 2px;
`

export const SalonTitle = styled.h2`
	font-size: 0.875rem;
	line-height: 1.25rem;
	font-weight: 500;
	margin-top: 0; // resetting global notino margin on heading
	margin-bottom: 0;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		font-size: 1rem;
		line-height: 1.5rem;
	}
`

export const LineWithIcon = styled.span`
	display: flex;
	gap: 4px;
	${NotinoBodySmall};

	& > svg {
		flex-shrink: 0;
		margin-top: 3px;
	}
`

export const OpeningHoursStatus = styled.span<{ $isOpen: boolean }>`
	color: ${(props) => (props.$isOpen ? theme.color.text.positive : theme.color.text.negative)};
`

export const OpeningHours = styled(LineWithIcon)<{ $isOpen: boolean }>`
	color: ${theme.color.text.tertiary};

	& svg {
		color: ${(props) => (props.$isOpen ? theme.color.icon.positive : theme.color.icon.negative)};
		width: 12px;
		height: 12px;
	}
`

export const Divider = styled.div`
	border-top: 1px solid rgba(225, 225, 225, 1);
	width: 100%;
`

export const ServicesList = styled.div`
	display: flex;
	flex-direction: column;
`

export const ServiceListItem = styled.div`
	display: flex;
	gap: 12px;
	align-items: flex-start;

	&:not(:last-child) {
		margin-bottom: 16px;
	}
`

export const ServiceImageWrapper = styled.div`
	flex-shrink: 0;
	width: 56px;
	height: 56px;
	overflow: hidden;
	border-radius: 4px;

	/* Override AppContainer img { width: 100% } — size is constrained by this wrapper. */
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`

export const ServiceImage = styled.img``

export const ServiceImageFallback = styled.div`
	width: 100%;
	height: 100%;
	background: ${theme.color.background.tertiary};
`

export const ServiceListItemBody = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;
`

export const ServiceListItemHeader = styled.div`
	display: flex;
	gap: 12px;
	align-items: flex-start;
`

export const ServiceListItemContent = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;
`

export const ServiceListItemTitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
`

export const ServiceListItemTitle = styled.div`
	${NotinoLabelRegular};
	color: ${theme.color.text.primary};
	min-width: 0;
`

export const ServiceStatusIconTrigger = styled.span`
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	cursor: default;
`

export const ClockIconWrapper = styled(ClockIcon)`
	color: #ff9500;
`
export const StatusTooltipContent = styled.div`
	${NotinoLabelRegular400};
	color: ${theme.color.text.primary};
	max-width: 240px;

	strong {
		${NotinoLabelRegular500};
		display: block;
		margin-bottom: 4px;
	}
`

export const ServiceMetaValue = styled.div`
	${NotinoLabelSmall400};
	color: ${theme.color.text.secondary};
`

export const ServiceListItemPrice = styled(RenderPrice)`
	${NotinoLabelRegular};
	color: ${theme.color.text.primary};
	white-space: nowrap;
`

export const ServiceAddToCalendarIconButton = styled.button`
	${resetButtonStyles};
	border: 2px solid ${theme.color.border.disabled};
	display: flex;
	border-radius: 999px;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 44px;
	height: 44px;
	color: ${theme.color.icon.primary};
	background: ${theme.color.background.primary};
	border: 2px solid #e6e6e6;
	&:hover {
		background: ${theme.color.background.disabled};
		transition: background 0.3s ease;
	}
`

export const DetailSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 4px;
`

export const EmailNotificationDisclaimer = styled.div`
	margin-top: 4px;
`

export const DetailSectionTitle = styled.h3`
	${NotinoLabelRegular500};
	color: ${theme.color.text.primary};
	margin: 0;
`

export const DetailSectionLines = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`

export const DetailSectionLine = styled.span<{ $muted?: boolean }>`
	${NotinoLabelRegular400};
	color: ${(props) => (props.$muted ? theme.color.text.secondary : theme.color.text.primary)};
	word-break: break-word;
`

export const DetailSectionText = styled.p`
	${NotinoLabelRegular400};
	color: ${theme.color.text.secondary};
	margin: 0;
	white-space: pre-wrap;
	word-break: break-word;
`

export const ServicePrice = styled(RenderPrice)`
	${NotinoLabelSmall};
	white-space: nowrap;
	display: block;
	margin-top: 4px;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		${NotinoLabelRegular};
	}
`

export const AddToCalendarButton = styled.button`
	${resetButtonStyles};
	${NotinoLabelSmall};
	margin-left: auto;
	text-decoration: underline;

	&:hover {
		text-decoration: none;
	}

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		${NotinoLabelRegular};
	}
`

export const Service = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		gap: 10px;
	}
`

export const ServiceValue = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;

	svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		gap: 10px;

		svg {
			width: 20px;
			height: 20px;
		}
	}
`

export const Footer = styled.div`
	display: none;

	@media (min-width: ${BREAKPOINTS.lg}) {
		display: flex;
		flex-shrink: 0;
		gap: 8px;
		position: sticky;
		bottom: 0;
		z-index: 2;
		margin: 0 -20px -20px;
		padding: 24px 20px 20px;
		background: ${theme.color.background.primary};
		border-top: 1px solid rgba(5, 5, 5, 0.06);
		box-shadow: 0 -4px 16px 0 rgba(0, 0, 0, 0.08);
	}
`

export const MobileFooter = styled.div`
	flex-shrink: 0;
	border-top: 1px solid rgba(5, 5, 5, 0.06);
	box-shadow: 0px -4px 16px 0px rgba(0, 0, 0, 0.08);
	z-index: 2;
	padding: 24px;
	display: flex;
	width: 100%;
	gap: 8px;
	background: ${theme.color.background.primary};

	@media (min-width: ${BREAKPOINTS.lg}) {
		display: none;
	}
`

export const EmployeeWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	word-break: break-word;
`

export const EmployeeAvatar = styled.img`
	width: 20px !important;
	height: 20px;
	border-radius: 50%;
	display: block;
`

export const SalonLogo = styled.div`
	position: absolute;
	top: 10px;
	right: 10px;
	width: 24px;
	height: 24px;
	@media (min-width: ${BREAKPOINTS.lg}) {
		width: 32px;
		height: 32px;
		top: 20px;
		right: 20px;
	}
	img {
		border-radius: 4px;
		display: block;
	}
`

export const LinkButton = styled(Button)`
	// fix for notino button when using href
	display: flex;
	align-items: center;

	justify-content: center;
	gap: 8px;
`
